import { Check, Loader2, Lock, ShieldCheck } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import logo from '../logo/lo.png';
import { WEB_APP_URL } from '../tracker/config';
import { DEGREES, YEARS } from './PathToPlacement';

// ── PATH TO PLACEMENT: the payment page (/p2p/pay, /p2p/pay?k=…) ─────────────
// Open to anyone: the plain address asks for the name, mobile, email, degree
// and passed-out year, and the server says what is due (payFind): a new
// student pays the ₹1,000 test fee; one who passed the test pays the ₹9,000
// balance, all of it or a part (at least ₹5,000) with the date they pick for the
// rest, which the Career Strategist or the admin approves. The ₹10,000 after
// placement is only mentioned here, not paid. Each P2P student also gets
// their own link (?k=…) by email or WhatsApp. The page opens Razorpay's
// checkout for the order and has the server check the payment
// (server/routes/p2p.js payInfo / payFind / payOrder / payVerify). Razorpay's
// webhook marks it paid too, if this page is closed before the check. The
// student ticks the terms and conditions first (the admin's, P2P Board →
// Terms); the receipt PDF that is emailed lists them too.

// Razorpay's checkout (window.Razorpay, from checkout.js)
type Checkout = new (options: Record<string, unknown>) => { open: () => void; on: (ev: string, cb: (r: any) => void) => void };
const checkout = (): Checkout | undefined => (window as any).Razorpay;

const PHONE = '9894449002';
const PHONE_LABEL = '98944 49002';
const CHECKOUT_JS = 'https://checkout.razorpay.com/v1/checkout.js';
const rupees = (n: number) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const thisYear = () => new Date().toLocaleDateString('en-IN', { year: 'numeric', timeZone: 'Asia/Kolkata' });
// "Tue, 15 Dec" (with the year when it isn't this one)
const day = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  const year = d.toLocaleDateString('en-IN', { year: 'numeric', timeZone: 'Asia/Kolkata' });
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', ...(year !== thisYear() ? { year: 'numeric' } : {}), timeZone: 'Asia/Kolkata' });
};
// YYYY-MM-DD in India, n days from today
const istDay = (n = 0) => new Date(Date.now() + 5.5 * 3600e3 + n * 86400e3).toISOString().slice(0, 10);

// balance: what is paid so far, what is due on this link, the smallest part,
// and the date the student asked for the rest (asked → the team's OK)
// terms: the admin's terms and conditions, one per line (older servers: none)
// nextFee: the balance after a pass (a test fee's link only); afterFee: what is
// paid after placement (ATS: none). Older servers send neither.
type Balance = { paid: number; total: number; due: number; minPart: number; maxDays: number; rest: { at: string; status: 'asked' | 'approved' } | null };
type Info = {
  ok: true; name: string; kind: 'test' | 'balance' | 'ats'; what: string; amount: number; status: 'Pending' | 'Paid' | 'Cancelled';
  payBy: string | null; paidAt: string | null; ready: boolean; terms?: string[]; balance?: Balance | null; nextFee?: number; afterFee?: number;
};
const ABOUT: Record<Info['kind'], string> = {
  test: 'Your Path to Placement test fee. If you don\'t pass the test, you get it back.',
  balance: 'The balance of your Path to Placement fee. Your 6 weeks start once all of it is paid.',
  ats: 'Your ATS resume. Our team makes it and emails it to you after the payment.',
};
// After the form: nothing to pay here
type Note = 'test' | 'talk' | 'done';
const NOTE: Record<Note, { title: string; text: string }> = {
  test: { title: 'Your test fee is paid ✅', text: 'Your test comes first. The balance opens here after you pass the test. Our Career Strategist will fix your test time with you.' },
  talk: { title: 'Please talk to your Career Strategist', text: 'There is nothing to pay here right now. Your Career Strategist will tell you the next step.' },
  done: { title: 'You have paid in full. Thank you!', text: 'Our team will be in touch about your placement.' },
};
// A part just paid: what is left and the date asked for it
type PartPaid = { amount: number; restAfter: number; restBy: string };

function loadCheckout(): Promise<void> {
  if (checkout()) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = CHECKOUT_JS;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Couldn't load the payment window. Check your internet and try again."));
    document.body.appendChild(s);
  });
}

async function api<T>(path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${WEB_APP_URL}/${path}`, body === undefined ? undefined : { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  } catch {
    throw new Error("Couldn't reach us. Check your internet and try again.");
  }
  const data = await res.json().catch(() => null);
  if (!data || !data.ok) throw new Error((data && data.error) || 'Something went wrong. Please try again.');
  return data as T;
}

// "1. …", "2) …", "• …": the list numbers the terms itself (as the receipt does)
const bareTerm = (t: string) => t.replace(/^\s*(?:\d{1,3}\s*[.):-]|[-•*·])\s*/, '');

const ink = '#1c1529', soft = '#5c5270', purple = '#4e3a69';
const inputStyle: React.CSSProperties = { display: 'block', width: '100%', minHeight: 50, padding: '0 16px', borderRadius: 14, border: '1.5px solid rgba(43,34,64,.18)', fontFamily: 'inherit', fontSize: 16, color: ink, background: '#fff', boxSizing: 'border-box' };
const labelStyle: React.CSSProperties = { display: 'block', margin: '14px 0 7px', fontSize: 13.5, fontWeight: 700, color: ink };
const errorStyle: React.CSSProperties = { margin: '14px 0 0', fontSize: 14, fontWeight: 600, lineHeight: 1.5, color: '#b23a10', background: '#fff1ec', borderRadius: 12, padding: '12px 14px' };
const h1Style: React.CSSProperties = { margin: '10px 0 0', fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em', color: ink };
const goldButton = (on: boolean): React.CSSProperties => ({
  marginTop: 18, width: '100%', minHeight: 54, border: 0, borderRadius: 999, background: '#F0B429', color: ink, fontFamily: 'inherit', fontSize: 16, fontWeight: 800,
  cursor: on ? 'pointer' : 'not-allowed', opacity: on ? 1 : 0.55, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9,
});

// The tick before paying, and the terms it agrees to (none set yet: the
// website's terms page)
function TermsAgree({ terms, agreed, onAgree }: { terms: string[]; agreed: boolean; onAgree: (v: boolean) => void }) {
  const [open, setOpen] = useState(false);
  const more: React.CSSProperties = { display: 'inline-block', marginTop: 6, marginLeft: 30, padding: 0, border: 0, background: 'none', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700, color: purple, textDecoration: 'underline', cursor: 'pointer' };
  return (
    <div style={{ marginTop: 16 }}>
      <label htmlFor="pay-agree" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 14, lineHeight: 1.5, color: ink }}>
        <input id="pay-agree" type="checkbox" checked={agreed} onChange={e => onAgree(e.target.checked)}
          style={{ width: 20, height: 20, margin: '1px 0 0', accentColor: '#2b2240', flexShrink: 0, cursor: 'pointer' }} />
        <span>I agree to the Terms &amp; Conditions of Path to Placement.</span>
      </label>
      {terms.length ? (
        <>
          <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-controls="pay-terms" style={more}>
            {open ? 'Hide the terms' : `Read the terms (${terms.length})`}
          </button>
          {open && (
            <ol id="pay-terms" style={{ margin: '10px 0 0', padding: '12px 14px 12px 34px', maxHeight: 280, overflowY: 'auto', borderRadius: 14, background: '#f7f4fb', fontSize: 13, lineHeight: 1.55, color: '#3d3450' }}>
              {terms.map((t, i) => <li key={i} style={{ marginTop: i ? 6 : 0 }}>{bareTerm(t)}</li>)}
            </ol>
          )}
        </>
      ) : (
        <a href="/Terms-and-Conditions" target="_blank" rel="noopener noreferrer" style={more}>Read the terms</a>
      )}
    </div>
  );
}

// The plain /p2p/pay address: the student's details. The server finds what
// they owe (a link) or says there is nothing to pay here (a note).
function PayForm({ onFound }: { onFound: (k: string) => void }) {
  const [f, setF] = useState({ name: '', phone: '', email: '', degree: '', passedOutYear: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState<{ note: Note; name: string } | null>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  const filled = !!(f.name.trim() && f.phone.trim() && f.email.trim() && f.degree && f.passedOutYear);
  const find = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!filled) { setError('Please fill in all five details.'); return; }
    setError('');
    setBusy(true);
    try {
      const r = await api<{ ok: true; k?: string; note?: Note; name?: string }>('p2p-pay-find', f);
      if (r.k) onFound(r.k);
      else if (r.note) setNote({ note: r.note, name: r.name || '' });
    } catch (err: any) { setError(err.message); }
    finally { setBusy(false); }
  };
  if (note) {
    const n = NOTE[note.note];
    return (
      <div role="status">
        <h1 style={h1Style}>{note.name ? `Hi ${note.name},` : n.title}</h1>
        {note.name && <p style={{ margin: '12px 0 0', fontSize: 17, fontWeight: 700, color: ink }}>{n.title}</p>}
        <p style={{ margin: '8px 0 0', fontSize: 14.5, lineHeight: 1.6, color: soft }}>{n.text}</p>
        <p style={{ margin: '12px 0 0', fontSize: 14.5, lineHeight: 1.6, color: soft }}>
          Questions? Call or WhatsApp <a href={`tel:${PHONE}`} style={{ color: purple, fontWeight: 700 }}>{PHONE_LABEL}</a>.
        </p>
        <button type="button" onClick={() => setNote(null)} style={{ marginTop: 16, padding: 0, border: 0, background: 'none', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, color: purple, textDecoration: 'underline', cursor: 'pointer' }}>
          ← Back to the form
        </button>
      </div>
    );
  }
  return (
    <form onSubmit={find} noValidate>
      <h1 style={h1Style}>Pay for Path to Placement</h1>
      <p style={{ margin: '8px 0 0', fontSize: 14.5, lineHeight: 1.6, color: soft }}>Fill in your details. We'll show what you need to pay: the test fee if you are new, or your balance after you pass the test.</p>
      <label htmlFor="pay-name" style={labelStyle}>Full name</label>
      <input id="pay-name" autoComplete="name" value={f.name} onChange={set('name')} style={inputStyle} />
      <label htmlFor="pay-phone" style={labelStyle}>Mobile number</label>
      <input id="pay-phone" type="tel" inputMode="tel" autoComplete="tel" value={f.phone} onChange={set('phone')} placeholder="10-digit number" style={inputStyle} />
      <label htmlFor="pay-email" style={labelStyle}>Email</label>
      <input id="pay-email" type="email" inputMode="email" autoComplete="email" value={f.email} onChange={set('email')} style={inputStyle} />
      <label htmlFor="pay-degree" style={labelStyle}>Degree</label>
      <select id="pay-degree" value={f.degree} onChange={set('degree')} style={{ ...inputStyle, color: f.degree ? ink : '#8d84a1' }}>
        <option value="">Pick your degree</option>
        {DEGREES.map(d => <option key={d} value={d}>{d}</option>)}
      </select>
      <label htmlFor="pay-year" style={labelStyle}>Passed-out year</label>
      <select id="pay-year" value={f.passedOutYear} onChange={set('passedOutYear')} style={{ ...inputStyle, color: f.passedOutYear ? ink : '#8d84a1' }}>
        <option value="">Pick the year</option>
        {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
      {error && <p role="alert" style={errorStyle}>{error}</p>}
      <button type="submit" disabled={busy} style={{ ...goldButton(!busy && filled), cursor: busy ? 'wait' : filled ? 'pointer' : 'not-allowed' }}>
        {busy ? <><Loader2 size={18} className="animate-spin" /> Checking…</> : 'Continue'}
      </button>
    </form>
  );
}

// The balance: all of it, or a part now and a date for the rest
function BalanceChoice({ b, part, amount, restBy, onPart, onAmount, onRestBy }: {
  b: Balance; part: boolean; amount: string; restBy: string;
  onPart: (v: boolean) => void; onAmount: (v: string) => void; onRestBy: (v: string) => void;
}) {
  const n = Number(amount.replace(/\D/g, '')) || 0;
  const left = b.due - n;
  const radio = (on: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderRadius: 14, cursor: 'pointer', fontSize: 15, fontWeight: 700, color: ink,
    border: `2px solid ${on ? '#2b2240' : 'rgba(43,34,64,.14)'}`, background: on ? '#f7f4fb' : '#fff',
  });
  return (
    <fieldset style={{ margin: '18px 0 0', padding: 0, border: 0 }}>
      <legend style={{ fontSize: 13.5, fontWeight: 700, color: ink, padding: 0, marginBottom: 8 }}>How much will you pay now?</legend>
      <label style={radio(!part)}>
        <input type="radio" name="pay-how" checked={!part} onChange={() => onPart(false)} style={{ accentColor: '#2b2240', width: 18, height: 18 }} />
        <span style={{ flex: 1 }}>Full balance</span><span>{rupees(b.due)}</span>
      </label>
      <label style={{ ...radio(part), marginTop: 8 }}>
        <input type="radio" name="pay-how" checked={part} onChange={() => onPart(true)} style={{ accentColor: '#2b2240', width: 18, height: 18 }} />
        <span style={{ flex: 1 }}>Part payment</span>
      </label>
      {part && (
        <div style={{ marginTop: 10, padding: '14px 16px', borderRadius: 14, background: '#f7f4fb' }}>
          <label htmlFor="pay-amount" style={{ ...labelStyle, marginTop: 0 }}>Amount now (at least {rupees(b.minPart)})</label>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 16, top: 14, fontSize: 16, fontWeight: 700, color: soft }}>₹</span>
            <input id="pay-amount" inputMode="numeric" value={amount} onChange={e => onAmount(e.target.value.replace(/\D/g, '').slice(0, 7))} placeholder={String(b.minPart)} style={{ ...inputStyle, paddingLeft: 34 }} />
          </div>
          {n > 0 && n < b.due && <p style={{ margin: '8px 0 0', fontSize: 14, fontWeight: 700, color: purple }}>Rest: {rupees(left)}</p>}
          <label htmlFor="pay-rest-by" style={labelStyle}>When will you pay the rest?</label>
          <input id="pay-rest-by" type="date" value={restBy} min={istDay(1)} max={istDay(b.maxDays || 366)} onChange={e => onRestBy(e.target.value)} style={inputStyle} />
          <p style={{ margin: '8px 0 0', fontSize: 12.5, lineHeight: 1.5, color: soft }}>Our team will confirm this date with you. Your 6 weeks start once the full balance is paid.</p>
        </div>
      )}
    </fieldset>
  );
}

export default function P2PPay() {
  const [params, setParams] = useSearchParams();
  const k = params.get('k') || '';
  const [info, setInfo] = useState<Info | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [paid, setPaid] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [part, setPart] = useState(false);
  const [partAmount, setPartAmount] = useState('');
  const [restBy, setRestBy] = useState('');
  const [partPaid, setPartPaid] = useState<PartPaid | null>(null);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Pay · Path to Placement | Vista Tech';
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex';
    document.head.appendChild(robots);
    return () => { document.title = prev; robots.remove(); };
  }, []);

  const load = useCallback(async () => {
    if (!k) { setInfo(null); return; }   // no link: the student fills the form (PayForm)
    setError('');
    try { setInfo(await api<Info>(`p2p-pay?k=${encodeURIComponent(k)}`)); } catch (e: any) { setError(e.message); }
  }, [k]);
  useEffect(() => { load(); }, [load]);

  const b = info?.kind === 'balance' ? info.balance || null : null;
  const canPart = !!b && b.due > b.minPart;
  const partN = Number(partAmount) || 0;
  // A part: at least the smallest part, less than what is due, with a date
  const partOk = !part || (partN >= (b?.minPart || 0) && partN < (b?.due || 0) && !!restBy);
  const payNow = part && b ? partN : info?.amount || 0;

  const pay = async () => {
    if (!info) return;
    if (!agreed) { setError('Please tick that you agree to the Terms & Conditions.'); return; }
    if (part && !partOk) { setError(`Type at least ${rupees(b?.minPart || 0)} and less than ${rupees(b?.due || 0)}, and pick the date for the rest.`); return; }
    setError('');
    setPartPaid(null);
    setBusy('Opening the payment window…');
    try {
      await loadCheckout();
      const o = await api<{ paid?: boolean; keyId: string; orderId: string; amount: number; currency: string; name: string; email: string; phone: string; description: string }>(
        'p2p-pay-order', { k, ...(part ? { amount: partN, restBy } : {}) });
      if (o.paid) { setPaid(true); setBusy(''); return; }
      const Razorpay = checkout();
      if (!Razorpay) throw new Error("Couldn't load the payment window. Check your internet and try again.");
      const rz = new Razorpay({
        key: o.keyId, order_id: o.orderId, amount: o.amount, currency: o.currency,
        name: 'Vista Tech', description: `Path to Placement · ${o.description}`, image: logo,
        prefill: { name: o.name, email: o.email, contact: o.phone ? `+91${o.phone}` : '' },
        theme: { color: '#2b2240' },
        handler: async (r: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }) => {
          setBusy('Confirming your payment…');
          try {
            const v = await api<{ status: 'Paid'; amount: number; part?: boolean; restAfter?: number; restBy?: string }>('p2p-pay-verify', { k, ...r });
            if (v.part) {
              // The rest has its own link now, on the same address: show it
              setPartPaid({ amount: v.amount, restAfter: v.restAfter || 0, restBy: v.restBy || '' });
              setPart(false);
              setPartAmount('');
              setRestBy('');
              await load();
            } else setPaid(true);
          } catch (e: any) { setError(e.message); }
          finally { setBusy(''); }
        },
        modal: { ondismiss: () => setBusy('') },
      });
      rz.on('payment.failed', (r: any) => setError(`The payment didn't go through${r?.error?.description ? `: ${r.error.description}` : ''}. You can try again.`));
      rz.open();
    } catch (e: any) {
      setError(e.message);
      setBusy('');
    }
  };

  const done = paid || info?.status === 'Paid';
  return (
    <div className="p2p-pay" style={{ minHeight: '100vh', background: 'linear-gradient(165deg,#130d1d 0%,#1d1430 55%,#2b2240 100%)', fontFamily: "'Plus Jakarta Sans', Inter, system-ui, sans-serif", padding: '28px 16px 40px' }}>
      {/* react-toastify's container is a <section>, which index.css pads on phones: a grey strip under the page */}
      <style>{'.p2p-pay ~ .Toastify { padding: 0 !important; }'}</style>
      <div style={{ maxWidth: 440, margin: '0 auto' }}>
        <Link to="/" aria-label="Vista Tech home" style={{ display: 'inline-flex', padding: '8px 14px', borderRadius: 14, background: '#fff' }}>
          {/* lo.png is a 500×500 canvas; the mark sits in the box (138,178)–(359,338) */}
          <span style={{ position: 'relative', display: 'block', width: Math.round(221 * 34 / 160), height: 34, overflow: 'hidden' }}>
            <img src={logo} alt="Vista Tech" style={{ position: 'absolute', left: -138 * 34 / 160, top: -178 * 34 / 160, width: 500 * 34 / 160, height: 500 * 34 / 160, maxWidth: 'none' }} />
          </span>
        </Link>
        <div style={{ marginTop: 22, background: '#fff', borderRadius: 26, padding: '28px 22px', boxShadow: '0 40px 80px -36px rgba(0,0,0,.7)' }}>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: purple }}>Path to Placement</p>
          {!k && <PayForm onFound={key => setParams({ k: key }, { replace: true })} />}
          {k && !info && !error && (
            <p style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 8, color: soft }}><Loader2 size={18} className="animate-spin" /> Loading…</p>
          )}
          {info && (
            <>
              <h1 style={h1Style}>{done ? `Thank you, ${info.name}!` : `Hi ${info.name},`}</h1>
              {done ? (
                <div role="status" style={{ marginTop: 18, textAlign: 'center' }}>
                  <span style={{ display: 'inline-grid', placeItems: 'center', width: 64, height: 64, borderRadius: '50%', background: '#1e7a4f', color: '#fff', boxShadow: '0 0 0 8px #e3f4ea' }}><Check size={30} strokeWidth={3} /></span>
                  <p style={{ margin: '18px 0 0', fontSize: 17, fontWeight: 700, color: ink }}>{rupees(info.amount)} paid · {info.what}</p>
                  <p style={{ margin: '8px 0 0', fontSize: 14.5, lineHeight: 1.6, color: soft }}>
                    {info.paidAt && !paid ? `Paid on ${day(info.paidAt)}. ` : ''}We have emailed you the receipt (a PDF, with the terms and conditions). Our team will be in touch about your next step.
                  </p>
                </div>
              ) : info.status === 'Cancelled' ? (
                <p style={{ margin: '14px 0 0', fontSize: 15, lineHeight: 1.6, color: soft }}>
                  This payment link is no longer active. If you still need to pay, ask our team for a new link: call or WhatsApp <a href={`tel:${PHONE}`} style={{ color: purple, fontWeight: 700 }}>{PHONE_LABEL}</a>.
                </p>
              ) : (
                <>
                  {partPaid && (
                    <div role="status" style={{ marginTop: 14, padding: '12px 14px', borderRadius: 14, background: '#e3f4ea', color: '#14532d', fontSize: 14, lineHeight: 1.55 }}>
                      <b>✅ {rupees(partPaid.amount)} paid.</b> We have emailed you the receipt. The rest, {rupees(partPaid.restAfter)}, is due by {day(partPaid.restBy)}: our team will confirm this date with you.
                    </div>
                  )}
                  <p style={{ margin: '8px 0 0', fontSize: 14.5, lineHeight: 1.6, color: soft }}>{ABOUT[info.kind]}</p>
                  <div style={{ marginTop: 18, borderRadius: 18, background: '#f7f4fb', padding: '16px 18px' }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: soft }}>{b && b.paid > 0 ? 'Still to pay' : info.what}</p>
                    <p style={{ margin: '4px 0 0', fontSize: 34, fontWeight: 800, letterSpacing: '-0.04em', color: '#2b2240' }}>{rupees(info.amount)}</p>
                    {b && b.paid > 0 && <p style={{ margin: '4px 0 0', fontSize: 13, fontWeight: 600, color: '#1e7a4f' }}>Paid so far: {rupees(b.paid)} of {rupees(b.total)}</p>}
                    {b && b.rest ? (
                      <p style={{ margin: '4px 0 0', fontSize: 13, fontWeight: 600, color: '#8a5a00' }}>
                        {b.rest.status === 'asked' ? `Your date: ${day(b.rest.at)} · waiting for our team's OK` : `Please pay by ${day(info.payBy || b.rest.at)}`}
                      </p>
                    ) : info.payBy && <p style={{ margin: '4px 0 0', fontSize: 13, fontWeight: 600, color: '#8a5a00' }}>Please pay by {day(info.payBy)}</p>}
                    {!!info.afterFee && (
                      <p style={{ margin: '10px 0 0', paddingTop: 10, borderTop: '1px solid #e6dff0', fontSize: 13, lineHeight: 1.5, fontWeight: 600, color: soft }}>
                        {info.nextFee ? `After you pass: ${rupees(info.nextFee)}. Then ${rupees(info.afterFee)} only after you are placed.` : `The other ${rupees(info.afterFee)} only after you are placed, not now.`}
                      </p>
                    )}
                  </div>
                  {!info.ready ? (
                    <p style={{ margin: '16px 0 0', fontSize: 14, lineHeight: 1.6, color: '#b23a10', background: '#fff1ec', borderRadius: 12, padding: '12px 14px' }}>
                      Online payment isn't ready yet. Please call or WhatsApp us on {PHONE_LABEL}.
                    </p>
                  ) : (
                    <>
                      {b && canPart && (
                        <BalanceChoice b={b} part={part} amount={partAmount} restBy={restBy}
                          onPart={v => { setPart(v); setError(''); }} onAmount={setPartAmount} onRestBy={setRestBy} />
                      )}
                      <TermsAgree terms={info.terms || []} agreed={agreed} onAgree={v => { setAgreed(v); if (v) setError(''); }} />
                      <button type="button" onClick={pay} disabled={!!busy || !agreed || !partOk}
                        style={{ ...goldButton(!busy && agreed && partOk && payNow > 0), minHeight: 56, fontSize: 16.5, cursor: busy ? 'wait' : agreed && partOk ? 'pointer' : 'not-allowed', boxShadow: '0 14px 30px -12px rgba(240,180,41,.6)' }}>
                        {busy ? <><Loader2 size={18} className="animate-spin" /> {busy}</> : <><Lock size={17} /> {payNow > 0 ? `Pay ${rupees(payNow)}` : 'Pay'}</>}
                      </button>
                      <p style={{ margin: '12px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 12.5, color: '#8d84a1' }}>
                        <ShieldCheck size={15} /> Secure payment by Razorpay · UPI, cards, netbanking
                      </p>
                    </>
                  )}
                </>
              )}
            </>
          )}
          {error && <p role="alert" style={errorStyle}>{error}</p>}
          {k && error && !info && (
            <button type="button" onClick={() => { setError(''); setParams({}, { replace: true }); }}
              style={{ marginTop: 14, padding: 0, border: 0, background: 'none', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, color: purple, textDecoration: 'underline', cursor: 'pointer' }}>
              Fill in your details instead
            </button>
          )}
        </div>
        <p style={{ marginTop: 18, textAlign: 'center', fontSize: 13, color: 'rgba(255,255,255,.7)' }}>
          Questions? Call or WhatsApp <a href={`tel:${PHONE}`} style={{ color: '#F0B429', fontWeight: 700 }}>{PHONE_LABEL}</a>
        </p>
      </div>
    </div>
  );
}
